import { createContext, useContext } from 'react'

import type { IOptionItem } from '~/client/src/shared/components/OptionsList/types'
import {
  ICardConfiguration,
  IResourceProperty,
  IResourceRecord,
  ResolveResourceCardLeadingSlot,
  ResolveResourceCardMetaItem,
  ResolveResourceCardSubtitle,
} from '~/client/src/shared/components/ResourceCardList/types'
import ViewMode from '~/client/src/shared/components/ViewSelector/ViewMode'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import {
  IFacet,
  IFacetOption,
} from '~/client/src/shared/hooks/useFilters/types'
import { IQuickFilters } from '~/client/src/shared/hooks/useResourceQuickFilters'
import { IResourceRuntimeContext } from '~/client/src/shared/resources/definitions'

export interface MobileResourceState {
  records: IResourceRecord[]
  filteredRecords: IResourceRecord[]
  properties: IResourceProperty[]
  cardConfiguration: ICardConfiguration
  getDisplayValue: (propertyName: string, value: any) => string
  resolveMetaItem?: ResolveResourceCardMetaItem
  resolveSubtitle?: ResolveResourceCardSubtitle
  resolveLeadingSlot?: ResolveResourceCardLeadingSlot
  groupConfig?: {
    groupBy: (record: IResourceRecord) => string | string[]
    getGroupLabel: (key: string, count: number) => string
  }
  searchKey: string
  appliedFilters: Record<string, Set<string>>
  isFilterShown: boolean
  grouping: string | null
  sortKey: string | null
  sortOrder: SortOrder
  loading: boolean
  selectedFilterCount: number
  selectedRecordId: string | null
}

export interface MobileResourceActions {
  setSearchKey: (value: string) => void
  openFilter: () => void
  closeFilter: () => void
  setGrouping: (value: string | null) => void
  setSortKey: (value: string | null) => void
  setSortOrder: (order: SortOrder) => void
  setFilter: (facetKey: string, selectedIds: string[]) => void
  resetAll: () => void
  selectRecord: (recordId: string) => void
  closeEdit: () => void
}

export interface MobileResourceMeta {
  resourceTypeId: string
  title: string
  icon: React.ComponentType<{ className?: string; size?: string | number }>
  viewModes: ViewMode[]
  countLabel?: (count: number) => string
  emptyMessage?: string
  facets: IFacet[]
  getFacetOptions: (facetKey: string) => IFacetOption[]
  groupingOptions: IOptionItem[]
  sortingOptions: IOptionItem[]
  initialSortKey: string | null
  initialSortOrder: SortOrder
  runtimeContext: IResourceRuntimeContext
  quickFilters: IQuickFilters
}

export interface MobileResourceContextValue {
  state: MobileResourceState
  actions: MobileResourceActions
  meta: MobileResourceMeta
}

export const MobileResourceContext =
  createContext<MobileResourceContextValue | null>(null)

export function useMobileResource(): MobileResourceContextValue {
  const context = useContext(MobileResourceContext)
  if (!context) {
    throw new Error('useMobileResource must be used within MobileResource')
  }
  return context
}

export function useMobileResourceState(): MobileResourceState {
  return useMobileResource().state
}

export function useMobileResourceActions(): MobileResourceActions {
  return useMobileResource().actions
}

export function useMobileResourceMeta(): MobileResourceMeta {
  return useMobileResource().meta
}
